import dayjs, { Dayjs } from 'dayjs';
import { CreateJobInput, JobAcceptType } from '@core/generated/gql-types';
/**
 * Calendar day from the job form (`YYYY-MM-DD` or a `Date` that may have come from Yup/JSON
 * parsing an ISO date-only string as UTC midnight). Always interpreted as **local** midnight
 * for that civil date — matches mobile `Date` / `startOf('day')` range anchors.
 */
export function parseScheduleDateLocal(input: string | Date): Dayjs {
  if (typeof input === 'string') {
    const trimmed = input.trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
    if (match) {
      const y = Number(match[1]);
      const mo = Number(match[2]) - 1;
      const d = Number(match[3]);
      return dayjs(new Date(y, mo, d, 0, 0, 0, 0));
    }
  }
  if (input instanceof Date) {
    return dayjs(
      new Date(
        input.getFullYear(),
        input.getMonth(),
        input.getDate(),
        0,
        0,
        0,
        0,
      ),
    );
  }
  return dayjs(input).startOf('day');
}

const minutesSinceMidnightFromDayjs = (d: Dayjs): number =>
  d.hour() * 60 + d.minute();

/**
 * Same rule as mobile `isOvernightClock`: end clock face strictly before start clock face.
 */
export const isOvernightClockInstants = (start: Dayjs, end: Dayjs): boolean =>
  minutesSinceMidnightFromDayjs(end) < minutesSinceMidnightFromDayjs(start);

export function reconcileJobCreateStartDateTime(params: {
  scheduleStartDay: string | Date;
  startTime: string;
}): Dayjs {
  const [h, m] = params.startTime.split(':').map((n) => Number(n) || 0);
  return parseScheduleDateLocal(params.scheduleStartDay)
    .hour(h)
    .minute(m)
    .second(0)
    .millisecond(0);
}

/**
 * Contract end instant on the inclusive last schedule evening, with overnight roll when needed.
 */
export function reconcileJobCreateEndDateTime(params: {
  scheduleStartDay: string | Date;
  scheduleLastEveningDay: string | Date;
  startTime: string;
  endTime: string;
}): Dayjs {
  const start = reconcileJobCreateStartDateTime({
    scheduleStartDay: params.scheduleStartDay,
    startTime: params.startTime,
  });
  const [eh, em] = params.endTime.split(':').map((n) => Number(n) || 0);
  let end = parseScheduleDateLocal(params.scheduleLastEveningDay)
    .hour(eh)
    .minute(em)
    .second(0)
    .millisecond(0);

  if (isOvernightClockInstants(start, end)) {
    end = end.add(1, 'day');
  }

  return end;
}

export type JobCreateContractInstants = {
  contractStart: Dayjs;
  contractEnd: Dayjs;
};

/**
 * Maps form schedule days + shift clocks to API contract instants.
 * `scheduleStartDate` / `scheduleEndDate` on the job are computed server-side on create.
 */
export function getJobCreateContractInstants(params: {
  /** Form: first calendar day of the schedule. */
  scheduleStartDay: string | Date;
  /** Form: inclusive last evening (end of date range). */
  scheduleLastEveningDay?: string | Date | null | '';
  startTime: string;
  endTime: string;
}): JobCreateContractInstants {
  const scheduleLastEvening =
    params.scheduleLastEveningDay != null &&
    params.scheduleLastEveningDay !== ''
      ? params.scheduleLastEveningDay
      : params.scheduleStartDay;

  const contractStart = reconcileJobCreateStartDateTime({
    scheduleStartDay: params.scheduleStartDay,
    startTime: params.startTime,
  });
  const contractEnd = reconcileJobCreateEndDateTime({
    scheduleStartDay: params.scheduleStartDay,
    scheduleLastEveningDay: scheduleLastEvening,
    startTime: params.startTime,
    endTime: params.endTime,
  });

  return { contractStart, contractEnd };
}

export function isShiftEndAfterStart(parent: {
  startDate?: string | Date;
  endDate?: string | Date;
  startTime?: string;
  endTime?: string;
}): boolean {
  const { startDate, endDate, startTime, endTime } = parent;
  if (!startDate || !startTime || !endTime) return true;
  const { contractEnd, contractStart } = getJobCreateContractInstants({
    scheduleStartDay: startDate,
    scheduleLastEveningDay: endDate,
    startTime,
    endTime,
  });
  return contractEnd.isAfter(contractStart);
}

export type BuildCreateJobInputParams = {
  clientId: string;
  title: string;
  description: string;
  classificationId: string;
  acceptType: JobAcceptType;
  contractStart: Dayjs;
  contractEnd: Dayjs;
  siteId: string;
  address?: CreateJobInput['address'];
  marginPercentage?: number;
  skipNotifications?: boolean;
};

/** Builds {@link CreateJobInput} without schedule anchor fields (server derives those). */
export function buildCreateJobInput(
  params: BuildCreateJobInputParams,
): CreateJobInput {
  return {
    clientId: params.clientId,
    title: params.title,
    description: params.description,
    classificationId: params.classificationId,
    acceptType: params.acceptType,
    startDate: String(params.contractStart.valueOf()),
    endDate: String(params.contractEnd.valueOf()),
    daysInput: {
      startTime: params.contractStart.format('HH:mm'),
      endTime: params.contractEnd.format('HH:mm'),
    },
    address: params.address,
    marginPercentage: params.marginPercentage,
    siteId: params.siteId,
    skipNotifications: params.skipNotifications,
  };
}
